import { memo } from 'react';
import { Link2 } from 'lucide-react';
import { cn } from '@cyphex/ui';
import type { LinkPreview } from '@cyphex/types';
import { AppLink } from '@/components/common/AppLink';

interface LinkPreviewCardProps {
  preview: LinkPreview;
  className?: string;
}

/** Server-side unfurl. Images are lazy with fixed intrinsic size — no layout shift. */
export const LinkPreviewCard = memo(function LinkPreviewCard({ preview, className }: LinkPreviewCardProps) {
  return (
    // The preview of a link INTO this app opens in the app, as the link does.
    <AppLink
      href={preview.url}
      className={cn(
        'flex max-w-bubble gap-3 rounded-lg border border-border bg-card p-2.5 transition-colors duration-fast hover:bg-subtle',
        className,
      )}
    >
      {preview.imageUrl ? (
        <img
          src={preview.imageUrl}
          alt=""
          width={96}
          height={72}
          loading="lazy"
          decoding="async"
          className="h-18 w-24 shrink-0 rounded-md object-cover"
        />
      ) : (
        <span className="flex h-18 w-24 shrink-0 items-center justify-center rounded-md bg-secondary">
          <Link2 size={20} className="text-muted-foreground" aria-hidden />
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-muted-foreground">
          {preview.siteName ?? new URL(preview.url).hostname}
        </span>
        <span className="mt-0.5 block truncate text-md font-semibold text-foreground">
          {preview.title ?? preview.url}
        </span>
        {preview.description ? (
          <span className="mt-0.5 line-clamp-2 block text-sm text-muted-foreground">
            {preview.description}
          </span>
        ) : null}
      </span>
    </AppLink>
  );
});
